import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dropdown } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import type { StaffMember, ShiftPreset, ShiftPresetData_Input, ProductionTask_Output, Product_Output, MachineVariant_Output } from '@/api'
import { StaffService } from '@/api'
import { toastAtom } from '@/atoms/toastAtom'
import type { ShiftAssignments, ShiftType, WeekOption } from '../hooks/useDetailedPlanFlow'
import { SHIFT_LABELS } from '../hooks/useDetailedPlanFlow'
import {
  buildStaffColorMap,
  buildStaffOptionsByShift,
  findStaffLocations,
  formatStaffLocations,
  getCategoryLabel,
  getStaffCapabilityWarnings,
  isStocker,
  LIMIT_HIGHLIGHT_MS,
  LIMIT_HIGHLIGHT_STYLE,
  OPERATOR_MACHINE_ID,
} from './staffUtils'

const WEEKDAY_LABELS_GRID = ['日', '月', '火', '水', '木', '金', '土'] as const

interface TaskDetailInfo {
  task: ProductionTask_Output
  product: Product_Output | undefined
  variant: MachineVariant_Output | undefined
}

interface ShiftAssignmentGridProps {
  week: WeekOption
  machineIds: string[]
  staffList: StaffMember[]
  assignments: ShiftAssignments
  productionTasks: ProductionTask_Output[]
  products: Product_Output[]
  onAssign: (date: string, shift: ShiftType, machineId: string, slotIndex: number, staffName: string | null) => void
  onApplyPreset: (date: string, data: ShiftPresetData_Input) => void
  onClearDate: (date: string) => void
  onBack: () => void
  onRunOptimizer: () => void
  isOptimizing: boolean
}

const SHIFTS: ShiftType[] = ['day', 'evening', 'night']

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土']

/** Format seconds to h:mm or m min display */
const formatDuration = (seconds: number): string => {
  const totalMinutes = Math.round(seconds / 60)
  if (totalMinutes >= 60) {
    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60
    return m > 0 ? `${h}h${String(m).padStart(2, '0')}m` : `${h}h`
  }
  return `${totalMinutes}m`
}

const ShiftAssignmentGrid: React.FC<ShiftAssignmentGridProps> = ({
  week,
  machineIds,
  staffList,
  assignments,
  productionTasks,
  products,
  onAssign,
  onApplyPreset,
  onClearDate,
  onBack,
  onRunOptimizer,
  isOptimizing,
}) => {
  const queryClient = useQueryClient()
  const toast = useAtomValue(toastAtom)

  // --- Preset state ---
  const [saveDialogVisible, setSaveDialogVisible] = useState(false)
  const [saveDialogDate, setSaveDialogDate] = useState<string | null>(null)
  const [savePresetName, setSavePresetName] = useState('')
  const [overwritePresetId, setOverwritePresetId] = useState<string | null>(null)

  const [loadDialogVisible, setLoadDialogVisible] = useState(false)
  const [loadDialogDate, setLoadDialogDate] = useState<string | null>(null)

  // --- Task detail modal state ---
  const [taskDetailInfo, setTaskDetailInfo] = useState<TaskDetailInfo | null>(null)

  // --- 「同日に2件まで」で弾いたスタッフの既存割当枠を一定時間強調する ---
  const [limitHighlight, setLimitHighlight] = useState<{ date: string; staffName: string } | null>(null)
  const limitHighlightTimerRef = useRef<number | null>(null)

  const showLimitHighlight = useCallback((date: string, staffName: string) => {
    setLimitHighlight({ date, staffName })
    if (limitHighlightTimerRef.current != null) window.clearTimeout(limitHighlightTimerRef.current)
    limitHighlightTimerRef.current = window.setTimeout(() => setLimitHighlight(null), LIMIT_HIGHLIGHT_MS)
  }, [])

  useEffect(
    () => () => {
      if (limitHighlightTimerRef.current != null) window.clearTimeout(limitHighlightTimerRef.current)
    },
    [],
  )

  // 強調した枠が横スクロールの外にあることがあるため、最初の枠を表示領域に入れる
  useEffect(() => {
    if (!limitHighlight) return
    document
      .querySelector('[data-limit-highlight="true"]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }, [limitHighlight])

  const isLimitHighlighted = (date: string, staffName: string | null) =>
    !!staffName && limitHighlight?.date === date && limitHighlight.staffName === staffName

  // --- Preset queries/mutations ---
  const presetsQuery = useQuery({
    queryKey: ['shift-presets'],
    queryFn: () => StaffService.listShiftPresets(),
    refetchOnWindowFocus: false,
  })

  const presets = presetsQuery.data?.presets ?? []

  const saveMutation = useMutation({
    mutationFn: (params: { name: string; data: ShiftPresetData_Input }) =>
      StaffService.saveShiftPreset({ name: params.name, data: params.data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-presets'] })
      setSaveDialogVisible(false)
      toast?.show({ severity: 'success', summary: '保存完了', detail: 'シフトプリセットを保存しました', life: 3000 })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (params: { id: string; name: string; data: ShiftPresetData_Input }) =>
      StaffService.updateShiftPreset(params.id, { name: params.name, data: params.data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-presets'] })
      setSaveDialogVisible(false)
      toast?.show({ severity: 'success', summary: '上書き完了', detail: 'シフトプリセットを上書き保存しました', life: 3000 })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => StaffService.deleteShiftPreset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-presets'] })
      toast?.show({ severity: 'info', summary: '削除完了', detail: 'シフトプリセットを削除しました', life: 3000 })
    },
  })

  const saveLatestShiftMutation = useMutation({
    mutationFn: () => {
      const weekdays: Record<string, ShiftPresetData_Input> = {}
      for (const date of week.dates) {
        const d = new Date(date)
        const label = WEEKDAY_LABELS_GRID[d.getDay()]
        const shiftData = assignments[date]
        if (shiftData) {
          weekdays[label] = {
            day: { ...shiftData.day },
            evening: { ...shiftData.evening },
            night: { ...shiftData.night },
          }
        }
      }
      return StaffService.saveLatestShift({ data: { weekdays } })
    },
    onSuccess: () => {
      toast?.show({ severity: 'success', summary: '保存完了', detail: 'シフトをサーバーに保存しました', life: 3000 })
    },
    onError: () => {
      toast?.show({ severity: 'error', summary: '保存失敗', detail: 'サーバーへの保存に失敗しました', life: 3000 })
    },
  })

  // --- Build current date's shift data for saving ---
  // Converts assignments to array format, sanitizing non-string values
  const buildPresetData = useCallback(
    (date: string): ShiftPresetData_Input => {
      const cleanShift = (record: Record<string, unknown> | undefined): Record<string, (string | null)[]> => {
        if (!record) return {}
        const out: Record<string, (string | null)[]> = {}
        for (const [k, v] of Object.entries(record)) {
          if (Array.isArray(v)) {
            out[k] = v.map((x) => (typeof x === 'string' ? x : null))
          } else {
            out[k] = [typeof v === 'string' ? v : null, null]
          }
        }
        return out
      }
      return {
        day: cleanShift(assignments[date]?.day as Record<string, unknown> | undefined),
        evening: cleanShift(assignments[date]?.evening as Record<string, unknown> | undefined),
        night: cleanShift(assignments[date]?.night as Record<string, unknown> | undefined),
      }
    },
    [assignments],
  )

  // --- Save handlers ---
  const handleOpenSaveDialog = useCallback((date: string) => {
    setSaveDialogDate(date)
    setSavePresetName('')
    setOverwritePresetId(null)
    setSaveDialogVisible(true)
  }, [])

  const handleSaveNew = useCallback(() => {
    if (!saveDialogDate || !savePresetName.trim()) return
    saveMutation.mutate({ name: savePresetName.trim(), data: buildPresetData(saveDialogDate) })
  }, [saveDialogDate, savePresetName, buildPresetData, saveMutation])

  const handleOverwrite = useCallback(() => {
    if (!saveDialogDate || !overwritePresetId) return
    const preset = presets.find((p) => p.id === overwritePresetId)
    if (!preset) return
    updateMutation.mutate({
      id: overwritePresetId,
      name: preset.name,
      data: buildPresetData(saveDialogDate),
    })
  }, [saveDialogDate, overwritePresetId, presets, buildPresetData, updateMutation])

  // --- Load handlers ---
  const handleOpenLoadDialog = useCallback((date: string) => {
    setLoadDialogDate(date)
    setLoadDialogVisible(true)
    presetsQuery.refetch()
  }, [presetsQuery])

  const handleLoadPreset = useCallback(
    (preset: ShiftPreset) => {
      if (!loadDialogDate) return
      onApplyPreset(loadDialogDate, preset.data)
      setLoadDialogVisible(false)
      toast?.show({ severity: 'success', summary: '呼出完了', detail: `「${preset.name}」を適用しました`, life: 3000 })
    },
    [loadDialogDate, onApplyPreset, toast],
  )

  const handleDeletePreset = useCallback(
    (id: string) => {
      deleteMutation.mutate(id)
    },
    [deleteMutation],
  )

  const handleClearDate = useCallback(
    (date: string) => {
      onClearDate(date)
      toast?.show({ severity: 'info', summary: 'クリア完了', detail: '当日のシフト割当をクリアしました', life: 2500 })
    },
    [onClearDate, toast],
  )

  // シフトごとにスタッフをフィルタリング
  const staffOptionsByShift = useMemo(() => buildStaffOptionsByShift(staffList), [staffList])

  const staffColorByName = useMemo(() => buildStaffColorMap(staffList), [staffList])

  // name → StaffMember lookup
  const staffByName = useMemo(() => {
    const map: Record<string, StaffMember> = {}
    for (const s of staffList) {
      map[s.name] = s
    }
    return map
  }, [staffList])

  const getStaffColorClass = useCallback(
    (name: string | null | undefined) => {
      if (!name) return 'bg-gray-200 text-700'
      return staffColorByName[name] ?? 'bg-surface-200 text-900'
    },
    [staffColorByName],
  )

  const staffCountsByDate = useMemo(() => {
    const out: Record<string, Record<string, number>> = {}
    for (const date of week.dates) {
      const counts: Record<string, number> = {}
      for (const shift of SHIFTS) {
        const shiftAssignments = assignments[date]?.[shift] ?? {}
        for (const slots of Object.values(shiftAssignments)) {
          for (const value of slots ?? []) {
            if (!value) continue
            counts[value] = (counts[value] ?? 0) + 1
          }
        }
      }
      out[date] = counts
    }
    return out
  }, [assignments, week.dates])

  const renderStaffOption = useCallback(
    (option: { label: string; value: string | null }) => (
      <div className={`px-2 py-0 border-round-sm ${getStaffColorClass(option.value)}`}>{option.label}</div>
    ),
    [getStaffColorClass],
  )

  const renderSelectedStaff = useCallback(
    (option: { label: string; value: string | null } | null) => {
      if (!option) {
        return <span className="text-500">未割り当て</span>
      }
      return (
        <div className={`px-2 py-0 border-round-sm inline-block ${getStaffColorClass(option.value)}`}>
          {option.label}
        </div>
      )
    },
    [getStaffColorClass],
  )

  // Build lookup: sku_id → Product_Output
  const productBySku = useMemo(() => {
    const map: Record<string, Product_Output> = {}
    for (const p of products) {
      map[p.sku_id] = p
      map[p.machine_sheet_sku_id ?? ''] =p
      // componentsも検索できるようにする
      for(const c of p.components){
        map[c.sku_id] = c
      }
    }
    return map
  }, [products])

  // Build lookup: date → machine_id → tasks[]
  const tasksByDateMachine = useMemo(() => {
    const map: Record<string, Record<string, ProductionTask_Output[]>> = {}
    for (const task of productionTasks) {
      const d = task.date
      if (!map[d]) map[d] = {}
      if (!map[d][task.machine_id]) map[d][task.machine_id] = []
      map[d][task.machine_id].push(task)
    }
    return map
  }, [productionTasks])

  /**
   * 同日・同シフトで requires_two_inspectors な機械のうち、
   * 検査員が2名以上割り当て済みの機械が1台でもあるかを返す。
   * key: `${date}__${shift}`
   */
  const twoInspectorSatisfiedByDateShift = useMemo(() => {
    const result: Record<string, boolean> = {}
    for (const date of week.dates) {
      for (const shift of SHIFTS) {
        let anySatisfied = false
        for (const mid of machineIds) {
          const mTasks = tasksByDateMachine[date]?.[mid] ?? []
          const needsTwo = mTasks.some((t) => {
            const p = productBySku[t.sku_id]
            return p?.machine_variants?.[mid]?.requires_two_inspectors === true
          })
          if (!needsTwo) continue
          const mSlots = assignments[date]?.[shift]?.[mid] ?? [null, null]
          if (mSlots.filter((s) => !!s && !isStocker(s)).length >= 2) {
            anySatisfied = true
            break
          }
        }
        result[`${date}__${shift}`] = anySatisfied
      }
    }
    return result
  }, [week.dates, machineIds, tasksByDateMachine, productBySku, assignments])

  /**
   * その日・その成形機の生産品に対して、割当スタッフ全員を横断して担当可否をチェックし警告文を返す。
   */
  const getStaffWarnings = useCallback(
    (staffNames: (string | null)[], date: string, machineId: string): string[] => {
      const tasks = tasksByDateMachine[date]?.[machineId] ?? []
      return getStaffCapabilityWarnings(staffNames, tasks.map((t) => t.sku_id), staffByName, productBySku)
    },
    [staffByName, tasksByDateMachine, productBySku],
  )

  const operatorStaffOptions = useMemo(
    () => [
      { label: '（未割当）', value: null },
      ...staffList
        .filter((s) => s.shift === 'operator')
        .map((s) => ({ label: s.name, value: s.name })),
    ],
    [staffList],
  )

  // Build row data: one row per shift
  const shiftRows = useMemo(
    () => SHIFTS.map((shift) => ({ shift, label: SHIFT_LABELS[shift] })),
    [],
  )

  return (
    <div className="mt-4">
      <p className="text-700 mb-3">
        {week.label} のシフト割当を行ってください。各成形機にスタッフを割り当てます。
      </p>

      {week.dates.map((date) => {
        const d = new Date(date)
        const dateLabel = `${date.slice(5).replace(/-/g, '/')} (${WEEKDAY_LABELS[d.getDay()]})`

        return (
          <div key={date} className="mb-4">
            <div className="flex align-items-center gap-2 mb-2">
              <h4 className="text-lg font-semibold m-0">{dateLabel}</h4>
              <Button
                icon="pi pi-save"
                label="保存"
                severity="success"
                size="small"
                outlined
                onClick={() => handleOpenSaveDialog(date)}
              />
              <Button
                icon="pi pi-folder-open"
                label="呼出"
                severity="info"
                size="small"
                outlined
                onClick={() => handleOpenLoadDialog(date)}
              />
              <Button
                icon="pi pi-times"
                label="クリア"
                severity="danger"
                size="small"
                outlined
                onClick={() => handleClearDate(date)}
              />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <DataTable
                value={shiftRows}
                size="small"
                showGridlines
                stripedRows
              >
                <Column
                  header="シフト"
                  field="label"
                  style={{ minWidth: '80px', fontWeight: 600 }}
                  frozen
                />
                {machineIds.map((machineId) => {
                  const tasks = tasksByDateMachine[date]?.[machineId] ?? []
                  const headerNode = (
                    <div className="flex flex-column">
                      <span className="font-bold">{machineId}</span>
                      {tasks.length > 0 && (
                        <div className="flex flex-column gap-0 mt-1">
                          {tasks.map((t) => {
                            const product = productBySku[t.sku_id]
                            const variant = product?.machine_variants?.[machineId]
                            const isDualOk = variant?.is_dual_inspection_compatible === true
                            const needsTwoInsp = variant?.requires_two_inspectors === true
                            const catLabel = getCategoryLabel(product)
                            return (
                              <span key={t.task_id} className="text-xs text-600 white-space-nowrap">
                                <a
                                  className="cursor-pointer text-primary hover:underline"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setTaskDetailInfo({ task: t, product, variant })
                                  }}
                                >
                                  {t.sku_id}
                                </a>
                                {' '}({formatDuration(t.duration)})
                                <span className={`ml-1 font-semibold ${catLabel.className}`}>{catLabel.label}</span>
                                {isDualOk && (
                                  <span className="ml-1 text-red-500 font-semibold">二台検査可</span>
                                )}
                                {needsTwoInsp && (
                                  <span className="ml-1 text-orange-600 font-semibold">検査員2名</span>
                                )}
                              </span>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                  return (
                  <Column
                    key={machineId}
                    header={headerNode}
                    style={{ minWidth: '250px', padding: '0.25rem' }}
                    body={(rowData: { shift: ShiftType }) => {
                      const slots = assignments[date]?.[rowData.shift]?.[machineId] ?? [null, null]
                      const isFullyUnassigned = slots.every((staffName) => !staffName)
                      const dateCounts = staffCountsByDate[date] ?? {}
                      const hasDoubleInspection = slots.some(
                        (staffName) => !!staffName && !isStocker(staffName) && (dateCounts[staffName] ?? 0) >= 2,
                      )
                      const requiresTwoInspectors = tasks.some((t) => {
                        const product = productBySku[t.sku_id]
                        const variant = product?.machine_variants?.[machineId]
                        return variant?.requires_two_inspectors === true
                      })
                      const assignedInspectorCount = slots.filter((s) => !!s && !isStocker(s)).length
                      const needsMoreInspectors = requiresTwoInspectors && assignedInspectorCount < 2
                        && !twoInspectorSatisfiedByDateShift[`${date}__${rowData.shift}`]
                      const cellWarnings = getStaffWarnings(slots, date, machineId)
                      return (
                        <div
                          className={`flex flex-column gap-1 border-round-sm p-1 ${
                            isFullyUnassigned ? 'bg-gray-200' : ''
                          }`}
                        >
                          {[0, 1].map((slotIndex) => {
                            const val = slots[slotIndex]
                            const displayValue = typeof val === 'string' ? val : null
                            const highlighted = isLimitHighlighted(date, displayValue)
                            return (
                              <div
                                key={slotIndex}
                                className={`flex flex-column border-round-sm ${highlighted ? 'bg-red-100' : ''}`}
                                style={highlighted ? LIMIT_HIGHLIGHT_STYLE : undefined}
                                data-limit-highlight={highlighted || undefined}
                              >
                                <Dropdown
                                  value={displayValue}
                                  options={staffOptionsByShift[rowData.shift]}
                                  onChange={(e) => {
                                    const v = e.value
                                    const resolved = typeof v === 'string' ? v : null
                                    const existsInSameMachine =
                                      !!resolved && slots.some((staffName: string | null, idx: number) => idx !== slotIndex && staffName === resolved)
                                    if (existsInSameMachine) {
                                      toast?.show({
                                        severity: 'warn',
                                        summary: '重複割当不可',
                                        detail: `${resolved} さんは同じ日・同じ機械に重複して割り当てできません`,
                                        life: 3000,
                                      })
                                      return
                                    }
                                    const currentCount = resolved ? (dateCounts[resolved] ?? 0) : 0
                                    if (resolved && !isStocker(resolved) && resolved !== displayValue && currentCount >= 2) {
                                      const locations = formatStaffLocations(
                                        findStaffLocations(assignments[date], resolved),
                                        SHIFT_LABELS,
                                      )
                                      toast?.show({
                                        severity: 'warn',
                                        summary: '割当上限',
                                        detail: `${resolved} さんは同日に2件までです（割当済み: ${locations}）`,
                                        life: LIMIT_HIGHLIGHT_MS,
                                      })
                                      showLimitHighlight(date, resolved)
                                      return
                                    }
                                    onAssign(date, rowData.shift, machineId, slotIndex, resolved)
                                  }}
                                  placeholder="未割り当て"
                                  className="w-full p-inputtext-sm"
                                  showClear
                                  itemTemplate={renderStaffOption}
                                  valueTemplate={renderSelectedStaff}
                                />
                              </div>
                            )
                          })}
                          {cellWarnings.length > 0 && (
                            <div className="flex flex-column">
                              {cellWarnings.map((w, wi) => (
                                <span key={wi} className="text-xs text-orange-600">
                                  ⚠ {w}
                                </span>
                              ))}
                            </div>
                          )}
                          {hasDoubleInspection ? (
                            <span className="text-red-500 text-xs font-semibold">二台検査</span>
                          ) : (
                            <span className="text-transparent text-xs">&nbsp;</span>
                          )}
                          {needsMoreInspectors && (
                            <span className="text-orange-600 text-xs font-semibold">⚠ 検査員2名必要</span>
                          )}
                        </div>
                      )
                    }}
                  />
                  )
                })}
                <Column
                  key="__operator__"
                  header={<span className="font-bold">オペレーター</span>}
                  style={{ minWidth: '200px', padding: '0.25rem' }}
                  body={(rowData: { shift: ShiftType }) => {
                    const slots = assignments[date]?.[rowData.shift]?.[OPERATOR_MACHINE_ID] ?? [null, null]
                    const isFullyUnassigned = slots.every((s) => !s)
                    return (
                      <div className={`flex flex-column gap-1 border-round-sm p-1 ${isFullyUnassigned ? 'bg-gray-200' : ''}`}>
                        {[0, 1].map((slotIndex) => {
                          const val = slots[slotIndex]
                          const displayValue = typeof val === 'string' ? val : null
                          const highlighted = isLimitHighlighted(date, displayValue)
                          return (
                            <div
                              key={slotIndex}
                              className={`flex flex-column border-round-sm ${highlighted ? 'bg-red-100' : ''}`}
                              style={highlighted ? LIMIT_HIGHLIGHT_STYLE : undefined}
                              data-limit-highlight={highlighted || undefined}
                            >
                              <Dropdown
                                value={displayValue}
                                options={operatorStaffOptions}
                                onChange={(e) => {
                                  const resolved = typeof e.value === 'string' ? e.value : null
                                  const existsInSameMachine =
                                    !!resolved && slots.some((s: string | null, idx: number) => idx !== slotIndex && s === resolved)
                                  if (existsInSameMachine) {
                                    toast?.show({
                                      severity: 'warn',
                                      summary: '重複割当不可',
                                      detail: `${resolved} さんは同じシフトのオペレーターに重複して割り当てできません`,
                                      life: 3000,
                                    })
                                    return
                                  }
                                  onAssign(date, rowData.shift, OPERATOR_MACHINE_ID, slotIndex, resolved)
                                }}
                                placeholder="未割り当て"
                                className="w-full p-inputtext-sm"
                                showClear
                                itemTemplate={renderStaffOption}
                                valueTemplate={renderSelectedStaff}
                              />
                            </div>
                          )
                        })}
                      </div>
                    )
                  }}
                />
              </DataTable>
            </div>
          </div>
        )
      })}

      <div className="flex justify-content-between mt-4">
        <Button
          label="戻る"
          icon="pi pi-arrow-left"
          severity="secondary"
          size="small"
          onClick={onBack}
        />
        <div className="flex gap-2">
          <Button
            label="サーバーに保存"
            icon="pi pi-cloud-upload"
            severity="info"
            size="small"
            outlined
            onClick={() => saveLatestShiftMutation.mutate()}
            loading={saveLatestShiftMutation.isPending}
          />
          <Button
            label="最適化実行"
            icon="pi pi-play"
            severity="success"
            size="small"
            onClick={onRunOptimizer}
            loading={isOptimizing}
          />
        </div>
      </div>

      {/* Save Dialog */}
      <Dialog
        header="シフトプリセットを保存"
        visible={saveDialogVisible}
        style={{ width: '480px' }}
        onHide={() => setSaveDialogVisible(false)}
        modal
      >
        <div className="flex flex-column gap-3">
          {/* Save as new */}
          <div>
            <label className="block mb-1 font-semibold">名前をつけて保存</label>
            <div className="flex gap-2">
              <InputText
                value={savePresetName}
                onChange={(e) => setSavePresetName(e.target.value)}
                placeholder="プリセット名を入力..."
                className="flex-1"
              />
              <Button
                label="保存"
                icon="pi pi-plus"
                size="small"
                onClick={handleSaveNew}
                disabled={!savePresetName.trim()}
                loading={saveMutation.isPending}
              />
            </div>
          </div>

          {/* Overwrite existing */}
          {presets.length > 0 && (
            <div>
              <label className="block mb-1 font-semibold">既存プリセットに上書き</label>
              <div className="flex gap-2">
                <Dropdown
                  value={overwritePresetId}
                  options={presets.map((p) => ({ label: p.name, value: p.id }))}
                  onChange={(e) => setOverwritePresetId(e.value)}
                  placeholder="上書き先を選択..."
                  className="flex-1"
                />
                <Button
                  label="上書き"
                  icon="pi pi-save"
                  size="small"
                  severity="warning"
                  onClick={handleOverwrite}
                  disabled={!overwritePresetId}
                  loading={updateMutation.isPending}
                />
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* Task Detail Dialog */}
      <Dialog
        header={taskDetailInfo ? `${taskDetailInfo.task.sku_id} - 製品詳細` : ''}
        visible={!!taskDetailInfo}
        style={{ width: '560px' }}
        onHide={() => setTaskDetailInfo(null)}
        modal
        draggable={false}
      >
        {taskDetailInfo && (() => {
          const { task, product, variant } = taskDetailInfo
          const defectRate = product?.defect_rate ?? 0
          const expectedDefects = Math.round(task.quantity * defectRate)
          const materialPerUnit = product?.material_per_unit_kg ?? null
          const totalMaterialKg = materialPerUnit != null ? task.quantity * materialPerUnit : null
          const qtyPerBox = product?.quantity_per_box ?? null
          const qtyPerPl = product?.quantity_per_pl ?? null
          const boxCount = qtyPerBox ? Math.ceil(task.quantity / qtyPerBox) : null
          const plCount = qtyPerPl ? Math.ceil(task.quantity / qtyPerPl) : null

          const fmt = (v: number | null | undefined) => {
            if (v == null) return '-'
            return Number.isFinite(v) ? v.toLocaleString('en-US') : '-'
          }
          const fmtF = (v: number | null | undefined, digits = 1) => {
            if (v == null) return '-'
            return Number.isFinite(v) ? v.toFixed(digits) : '-'
          }
          const fmtBool = (v: boolean | null | undefined) => {
            if (v == null) return '-'
            return v ? '〇' : '×'
          }

          const liddedLabel =
            product?.is_lidded_or_appearance === 'lidded' ? '便蓋品' :
            product?.is_lidded_or_appearance === 'appearance' ? '外観品' :
            product?.is_lidded_or_appearance != null ? product.is_lidded_or_appearance : '未分類'

          return (
            <div className="flex flex-column gap-3">
              {/* Product info */}
              {product && (
                <div className="flex flex-column gap-1">
                  <div className="text-sm text-500">{product.category}</div>
                  <div className="font-semibold">{product.name}</div>
                  <div className="flex gap-2 align-items-center mt-1">
                    <span className="text-xs text-600">分類:</span>
                    <span className="text-xs font-medium">{liddedLabel}</span>
                  </div>
                </div>
              )}

              {/* Production task info */}
              <div>
                <div className="text-sm font-semibold mb-1 text-700">生産タスク</div>
                <div className="grid text-sm">
                  <div className="col-6 flex justify-content-between"><span className="text-600">成形機</span><span>{task.machine_id}</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">日付</span><span>{task.date}</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">数量</span><span>{fmt(task.quantity)}</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">総量</span><span>{fmt(task.quantity_total)}</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">不良率</span><span>{fmtF(defectRate * 100)}%</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">予測不良数</span><span>{fmt(expectedDefects)}</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">生産時間</span><span>{formatDuration(task.duration)}</span></div>
                </div>
              </div>

              {/* Material / packaging calc */}
              {product && (
                <div>
                  <div className="text-sm font-semibold mb-1 text-700">材料・梱包</div>
                  <div className="grid text-sm">
                    <div className="col-6 flex justify-content-between"><span className="text-600">材料1</span><span>{product.material1 ?? '-'}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">材料2</span><span>{product.material2 ?? '-'}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">材料原単位(kg)</span><span>{fmtF(materialPerUnit, 4)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">材料使用量(kg)</span><span>{fmtF(totalMaterialKg, 1)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">入数/箱</span><span>{fmt(qtyPerBox)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">箱数</span><span>{fmt(boxCount)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">入数/PL</span><span>{fmt(qtyPerPl)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">PL数</span><span>{fmt(plCount)}</span></div>
                  </div>
                </div>
              )}

              {/* Machine variant info */}
              {variant && (
                <div>
                  <div className="text-sm font-semibold mb-1 text-700">成形機情報 ({variant.machine_id})</div>
                  <div className="grid text-sm">
                    <div className="col-6 flex justify-content-between"><span className="text-600">サイクルタイム(s)</span><span>{fmtF(variant.cycle_time_sec)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">組立工程</span><span>{fmtBool(variant.requires_assembly)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">二台検査可</span><span>{fmtBool(variant.is_dual_inspection_compatible)}</span></div>
                    {variant.note && (
                      <div className="col-12">
                        <span className="text-600">成形方法：</span>
                        <span className="text-700">{variant.note}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })()}
      </Dialog>

      {/* Load Dialog */}
      <Dialog
        header="シフトプリセットを呼び出し"
        visible={loadDialogVisible}
        style={{ width: '520px' }}
        onHide={() => setLoadDialogVisible(false)}
        modal
      >
        {presets.length === 0 ? (
          <p className="text-500">保存されたプリセットはありません。</p>
        ) : (
          <DataTable value={presets} size="small" stripedRows>
            <Column field="name" header="プリセット名" />
            <Column
              header="操作"
              style={{ width: '180px' }}
              body={(rowData: ShiftPreset) => (
                <div className="flex gap-1">
                  <Button
                    label="適用"
                    icon="pi pi-check"
                    size="small"
                    onClick={() => handleLoadPreset(rowData)}
                  />
                  <Button
                    icon="pi pi-trash"
                    size="small"
                    severity="danger"
                    outlined
                    onClick={() => handleDeletePreset(rowData.id)}
                    loading={deleteMutation.isPending}
                  />
                </div>
              )}
            />
          </DataTable>
        )}
      </Dialog>
    </div>
  )
}

export default ShiftAssignmentGrid
